import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Modal from 'tbk-components/src/components/Modal';
import StatBarStat, { IStatBarStatProps } from '../Stat';
import AnimatedArcs from '../../common/AnimatedArcs';

export interface IMovingTilesI18nProps {
	play: string;
	pause: string;
}
export interface IMovingTilesTilesProps {
	heading?: string;
	/**
	 * @control textarea
	 */
	description?: string;
	image?: IImage;
	cta?: ILink;
}

export interface IMovingTilesCarouselProps {
	carousel?: IMovingTilesTilesProps[];
	/**
	 * This is the total time for a carousel row to finish one loop.
	 * Each tile has a 25s (at viewport width of 1400px) show time by DEFAULT if you leave the field blank.
	 * The total time for a carousel row to finish a loop is the number of tiles in a row multiplied by 25s.
	 */
	carouselSpeed?: number;
	carouselDirection?: 'Right' | 'Left';
}

export interface IMovingTilesProps extends IBaseElementProps {
	heading?: string;
	/**
	 * @control textarea
	 */
	description?: string;
	stats: IStatBarStatProps[];
	/**
	 * Choose this option to automatically create carousels from the BU content.
	 * The auto-created carousels override the custom tiles.
	 */
	useAutomaticCarousels?: boolean;
	carousels?: IMovingTilesCarouselProps[];
	enableLineAnimations?: boolean;
	cta?: ILink;
	i18n?: IMovingTilesI18nProps;
	inverted?: boolean;
}

/**
 * Calian Moving Tiles
 * @icon format-gallery
 * @block
 */
const MovingTiles: React.FC<IMovingTilesProps> = ({
	id,
	className,
	classNames = [],
	heading,
	description,
	stats,
	carousels,
	enableLineAnimations = false,
	cta,
	i18n = {
		play: 'Play',
		pause: 'Pause',
	},
	inverted = false,
}) => {
	const ref = useRef<HTMLDivElement>(null);
	const [carouselRows, setCarouselRows] = useState(carousels);
	const [screenWidth, setScreenWidth] = useState(0);
	const [startAnimation, setStartAnimation] = useState<boolean>(true);
	const [showModal, setShowModal] = useState(false);
	const [modalHeading, setModalHeading] = useState<string>('');
	const [modalDescription, setModalDescription] = useState<string>('');
	const [modalCTALabel, setModalCTALabel] = useState<string>('');
	const [modalCTAHref, setModalCTAHref] = useState<string>('');
	const [modalCTATarget, setModalCTATarget] = useState<string>('');
	const [modalImage, setModalImage] = useState<string>('');

	useEffect(() => {
		if (!carousels || !ref.current) {
			return;
		}

		const carouselLen = carousels.map((item) => item.carousel.length);
		// limit the max number of tiles per carousel row to 22
		// limit the min number of tiles per carousel row to 4
		const maxTiles =
			Math.max(...carouselLen) > 22
				? 22
				: Math.max(...carouselLen) < 4
				? 4
				: Math.max(...carouselLen);
		const modifyCarousels = carousels
			.map((item) => {
				const original = item.carousel;

				if (original.length <= 0) {
					return null;
				}

				let slicedCarousel;

				if (original.length > maxTiles) {
					// remove the items after the maxTiles item
					slicedCarousel = original.slice(0, maxTiles);
				} else {
					const clonedCarousel = [];

					// clone the carousel items until it has the max number of tiles
					while (clonedCarousel.length < maxTiles) {
						const carouselToClone =
							original[clonedCarousel.length % original.length];
						clonedCarousel.push(carouselToClone);
					}

					slicedCarousel = clonedCarousel;
				}

				return {
					...item,
					carousel: slicedCarousel,
				};
			})
			.filter((c) => c !== null); // filter out null values

		setCarouselRows(modifyCarousels);

		const handleTiles = () => {
			const panes = ref.current?.querySelectorAll(
				'.calian-moving-tiles__pane--first a',
			) as NodeListOf<HTMLElement>;

			panes.forEach((pane) => {
				pane.setAttribute('tabIndex', '-1');
			});
		};

		const handleResize = () => {
			setScreenWidth(window.innerWidth);
		};

		handleTiles();
		handleResize();
		window.addEventListener('load', handleTiles);
		window.addEventListener('load', handleResize);
		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('load', handleTiles);
			window.removeEventListener('load', handleResize);
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const loopPane = (
		carousel: IMovingTilesTilesProps[],
		carouselOrder: number,
		paneOrder: number,
		carouselSpeed: number,
		carouselDirection: 'Right' | 'Left',
	) => {
		const content = (
			<div
				className={
					className ||
					cn(
						'd-flex calian-moving-tiles__pane calian-moving-tiles__pane--animate',
						paneOrder % 2 != 0 &&
							'calian-moving-tiles__pane--first',
						paneOrder % 2 == 0 &&
							'calian-moving-tiles__pane--second',
						carouselOrder % 2 != 0 &&
							'calian-moving-tiles__pane--even',
						!startAnimation && 'calian-moving-tiles__pane--pause',
						carousel.length === 1 &&
							'calian-moving-tiles__pane--one',
						carousel.length === 2 &&
							'calian-moving-tiles__pane--two',
						carousel.length === 3 &&
							'calian-moving-tiles__pane--three',
						...classNames,
					)
				}
				style={{
					width:
						screenWidth < 1400
							? `${carousel.length * 412}px`
							: `${carousel.length * (screenWidth / 4 + 24)}px`,
					animationDuration: carouselSpeed
						? `${carouselSpeed}s`
						: `${carousel.length * 25}s`, // every tile has 25 seconds on the screen
					animationDirection:
						carouselDirection && carouselDirection === 'Left'
							? `normal`
							: 'reverse',
				}}
			>
				{carousel.map(
					(
						{
							heading,
							description,
							image,
							cta,
						}: IMovingTilesTilesProps,
						index: number,
					) => (
						<div
							className="calian-moving-tiles__tile pe-3 pe-xxl-4"
							key={index}
						>
							<button
								onClick={() => {
									setShowModal(true);
									setStartAnimation(false);
									setModalHeading(heading);
									setModalDescription(description);
									setModalCTALabel(cta.label);
									setModalCTAHref(cta.href);
									setModalCTATarget(cta.target);
									setModalImage(image.src);
								}}
								className="d-flex text-decoration-none border-0 p-0 align-items-center"
							>
								<div className="calian-moving-tiles__tile-heading mx-5 my-4 me-3 text-left">
									{heading}
								</div>
								<div className="calian-moving-tiles__tile-image ms-auto">
									<Image {...image} />
								</div>
							</button>
						</div>
					),
				)}
			</div>
		);
		return content;
	};

	return (
		<div
			id={id}
			className={cn(
				'calian-moving-tiles position-relative',
				inverted && 'calian-moving-tiles--inverted',
			)}
			ref={ref}
		>
			<div
				className={cn(
					'calian-moving-tiles__container position-relative pt-5 pb-9 py-lg-12',
					!cta?.href &&
						'calian-moving-tiles__container--no-cta pb-lg-12',
				)}
			>
				<div className="calian-moving-tiles__content mx-auto position-relative z-2 px-3">
					{heading && <h2 className="text-center">{heading}</h2>}
					{description && (
						<div
							dangerouslySetInnerHTML={{
								__html: description,
							}}
							className="text-center"
						/>
					)}
				</div>
				{stats && stats.length > 0 && (
					<div className="calian-moving-tiles__stats position-relative mx-auto mt-4 mt-lg-6 z-2 d-flex flex-column flex-md-row align-items-center justify-content-center">
						{stats.map((stat, index) => (
							<div key={index}>
								<StatBarStat {...stat} />
							</div>
						))}
					</div>
				)}
				{carousels && carousels.length > 0 && (
					<div className="calian-moving-tiles__carousels position-relative z-2 d-flex flex-column gap-3 gap-md-5 mt-5 mt-lg-12">
						{carouselRows.map(
							(
								{ carousel, carouselSpeed, carouselDirection },
								index,
							) =>
								carousel &&
								carousel.length > 0 && (
									<div
										key={index}
										className="calian-moving-tiles__carousel"
										style={{
											width:
												screenWidth < 1400
													? `${
															carousel.length *
															412
													  }px`
													: `${
															carousel.length *
															(screenWidth / 4 +
																24)
													  }px`,
										}}
									>
										{loopPane(
											carousel,
											index % 2 != 0 ? 1 : 2,
											1,
											carouselSpeed,
											carouselDirection,
										)}
										{loopPane(
											carousel,
											index % 2 != 0 ? 1 : 2,
											2,
											carouselSpeed,
											carouselDirection,
										)}
									</div>
								),
						)}
					</div>
				)}
				{cta?.href && (
					<div className="calian-moving-tiles__cta position-relative d-flex justify-content-center mt-5 px-3 mt-lg-9 z-2">
						<Button
							className={cn(
								`btn btn-${inverted ? 'primary' : 'secondary'}`,
							)}
							{...cta}
						/>
					</div>
				)}
				{carousels && carousels.length > 0 && (
					<Button
						label={startAnimation ? i18n.pause : i18n.play}
						onClick={() => setStartAnimation(!startAnimation)}
						className={cn(
							'calian-moving-tiles__control position-absolute border-0 bg-transparent z-2',
							startAnimation &&
								'calian-moving-tiles__control--pause',
						)}
					/>
				)}
			</div>
			{enableLineAnimations && (
				<AnimatedArcs
					arcsStyle={'Component - Two Lines'}
					arcsPosition="Right"
					arcOneColor="var(--bs-white)"
					arcOneStrokeWidth={4}
					arcTwoColor={`var(--bs-${
						inverted ? 'secondary' : 'primary'
					})`}
					arcTwoStrokeWidth={3}
					animationDelay={250}
					animationOffset={150}
				/>
			)}
			{showModal && (
				<Modal
					onCancel={function () {
						setShowModal(false);
						setStartAnimation(true);
					}}
					modalClassNames={[
						'calian-moving-tiles__modal',
						'modal-dialog',
						'modal-dialog-centered',
						'modal-lg',
						inverted && 'inverted',
					]}
					headerClassNames={[
						'border-0 position-absolute w-100 top-0 right-0 z-3',
					]}
					bodyClassNames={['modal-body']}
					trapFocus
				>
					<div className="d-flex gap-6 gap-lg-7 flex-column flex-md-row">
						<div className="d-flex flex-column gap-3 order-2 order-lg-1">
							{modalHeading && (
								<h3 className="mb-0">{modalHeading}</h3>
							)}
							{modalDescription && (
								<div
									dangerouslySetInnerHTML={{
										__html: modalDescription,
									}}
								/>
							)}
							<div className="mt-2">
								<a
									href={modalCTAHref}
									target={modalCTATarget}
									rel={
										modalCTATarget === '_blank'
											? 'noopener noreferrer'
											: ''
									}
									className="btn btn-sm btn-primary"
								>
									{modalCTALabel}
								</a>
							</div>
						</div>
						<div className="modal-image order-1 order-lg-2">
							<img src={modalImage} alt={modalHeading} />
						</div>
					</div>
				</Modal>
			)}
		</div>
	);
};
export default MovingTiles;
